
import { useEffect, useRef } from 'react';
import Button from '../../../components/base/Button';

const HeroSection = () => {
  const gridRef = useRef<HTMLDivElement>(null);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Nur Ihre eigenen Bilder und Booking.com Bilder
  const backgroundImages = [
    // Mobilheim Bilder
    'https://img.webme.com/pic/j/jo-allround/20220603_132911.jpg',
    'https://img.webme.com/pic/j/jo-allround/20220603_133607.jpg',
    'https://img.webme.com/pic/j/jo-allround/20220629_174201.jpg',
    'https://img.webme.com/pic/j/jo-allround/Bild Mobilheim für Text.jpg',
    // Booking.com Ferienwohnung Bilder
    'https://cf.bstatic.com/xdata/images/hotel/max1024x768/469725592.jpg?k=f3e9d5f2a74343c04740cb13a66b8334eca55adc98c4ef287a58abc2ca24acac&o=&hp=1',
    'https://cf.bstatic.com/xdata/images/hotel/max500/469726948.jpg?k=95abd7491cda9c4187571cbd51868f154d7a147d3bd339e25d4d1078474016c7&o=',
    'https://cf.bstatic.com/xdata/images/hotel/max500/469727464.jpg?k=ac4b498776f61bd132e022eb152fe5c7dd76faa49bdfc52df68ca41f3efaf262&o=',
    'https://cf.bstatic.com/xdata/images/hotel/max1024x768/469726319.jpg?k=39ce978d601e8819dd40e5a973776868a725493787d20c4d26c2c2c2680a6322&o='
  ];

  useEffect(() => {
    if (!gridRef.current) return;

    const grid = gridRef.current;
    const items = grid.querySelectorAll('.grid-item');
    
    // Animation für die Grid-Items
    const animateItems = () => {
      items.forEach((item, index) => {
        const delay = index * 100;
        const duration = 3000 + Math.random() * 2000;
        
        setTimeout(() => {
          const animate = () => {
            const randomX = (Math.random() - 0.5) * 20;
            const randomY = (Math.random() - 0.5) * 20;
            const randomScale = 0.95 + Math.random() * 0.1;
            const randomRotate = (Math.random() - 0.5) * 5;
            
            (item as HTMLElement).style.transform = `translate(${randomX}px, ${randomY}px) scale(${randomScale}) rotate(${randomRotate}deg)`;
            
            setTimeout(() => {
              (item as HTMLElement).style.transform = 'translate(0px, 0px) scale(1) rotate(0deg)';
            }, duration / 2);
          };
          
          animate();
          setInterval(animate, duration);
        }, delay);
      });
    };

    // Starte Animation nach kurzer Verzögerung
    setTimeout(animateItems, 1_000);

    return () => {
      items.forEach(item => {
        (item as HTMLElement).style.transform = 'translate(0px, 0px) scale(1) rotate(0deg)';
      });
    };
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animierter Hintergrund */}
      <div 
        ref={gridRef}
        className="absolute inset-0 grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 p-4 opacity-30"
      >
        {backgroundImages.map((image, index) => (
          <div
            key={index}
            className="grid-item aspect-square rounded-lg overflow-hidden transition-transform duration-1000 ease-in-out"
            style={{
              animationDelay: `${index * 0.1}s`
            }}
          >
            <img
              src={image}
              alt={`Handwerk und Ferienunterkünfte ${index + 1}`}
              className="w-full h-full object-cover brightness-110"
              loading="lazy"
            />
          </div>
        ))}
        {/* Zusätzliche Items für bessere Abdeckung */}
        {backgroundImages.map((image, index) => (
          <div
            key={`extra-${index}`}
            className="grid-item aspect-square rounded-lg overflow-hidden transition-transform duration-1000 ease-in-out"
            style={{
              animationDelay: `${(index + backgroundImages.length) * 0.1}s`
            }}
          >
            <img
              src={image}
              alt={`Handwerk und Ferienunterkünfte Extra ${index + 1}`}
              className="w-full h-full object-cover brightness-110"
              loading="lazy"
            />
          </div>
        ))}
      </div>

      {/* Overlay für bessere Lesbarkeit */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-black/75 to-green-900/50"></div>

      {/* Hauptinhalt */}
      <div className="container mx-auto px-4 text-center text-white relative z-10">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight animate-fade-in">
            Professionelle <span className="text-green-400">Handwerksleistungen</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200 animate-fade-in-delay">
            Von der Planung bis zur Vollendung - Ihr zuverlässiger Partner für alle Bau- und Renovierungsprojekte
          </p>
          
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-black/40 backdrop-blur-sm rounded-lg p-6 border border-green-500/30 animate-slide-up">
              <div className="text-4xl font-bold text-green-400 mb-2">30+</div>
              <div className="text-lg text-gray-200">Jahre Erfahrung</div>
            </div>
            <div className="bg-black/40 backdrop-blur-sm rounded-lg p-6 border border-green-500/30 animate-slide-up-delay">
              <div className="text-4xl font-bold text-green-400 mb-2">25</div>
              <div className="text-lg text-gray-200">Jahre bestehen der Firma</div>
            </div>
            <div className="bg-black/40 backdrop-blur-sm rounded-lg p-6 border border-green-500/30 animate-slide-up-delay-2">
              <div className="text-4xl font-bold text-green-400 mb-2">400+</div>
              <div className="text-lg text-gray-200">Abgeschlossene Projekte</div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-delay-2">
            <Button
              onClick={() => scrollToSection('services')}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              Unsere Leistungen
            </Button>
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-transparent border-2 border-green-400 text-green-400 hover:bg-green-400 hover:text-black px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Kontakt aufnehmen
            </Button>
          </div>
        </div>
      </div>

      {/* Zusätzliche CSS-Animationen */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(50px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fadeIn 1s ease-out forwards;
        }
        
        .animate-fade-in-delay {
          animation: fadeIn 1s ease-out 0.3s forwards;
          opacity: 0;
        }
        
        .animate-fade-in-delay-2 {
          animation: fadeIn 1s ease-out 0.6s forwards;
          opacity: 0;
        }
        
        .animate-slide-up {
          animation: slideUp 0.8s ease-out 0.8s forwards;
          opacity: 0;
        }
        
        .animate-slide-up-delay {
          animation: slideUp 0.8s ease-out 1s forwards;
          opacity: 0;
        }
        
        .animate-slide-up-delay-2 {
          animation: slideUp 0.8s ease-out 1.2s forwards;
          opacity: 0;
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
